import { useEffect, useState } from 'react';

import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import WarningIconModal from '@/components/WarningIconModal/WarningIconModal';
import { URLS } from '@/constants/urls';
import { MINOR_LEGAL_CAPACITY, ADULT_LEGAL_CAPACITY_KO, LEGAL_CAPACITY_CATEGORY } from '@/enums/userLegalCapacity';
import { useNavigationToKYC } from '@/hooks/useNavigationToKYC';
import { ContentModalKycProps, TitleType } from '@/types/modalKycTypes';
import { sendLogEvent } from '@/utils/functions/tracking/sendLogEvent';

import { CONTENT, LOCATION_KEYS, URL_PDF_KYC_MAJOR, URL_PDF_KYC_MINOR, TITLES } from './dico';
import style from './styles.module.css';

const ContentModalKyc: React.FC<ContentModalKycProps> = ({
  onClickClose,
  WarningIcon,
  legalCapacity,
  variant,
  location,
  investmentType,
  contractName,
}) => {
  const { navigateToKYC } = useNavigationToKYC();
  const [legalCapacityCategory, setLegalCapacityCategory] = useState<LEGAL_CAPACITY_CATEGORY>(
    LEGAL_CAPACITY_CATEGORY.adultOK
  );
  const [title, setTitle] = useState<TitleType>(TITLES.default);
  const [content, setContent] = useState<(typeof CONTENT)[keyof typeof CONTENT]>(CONTENT.empty);

  useEffect(() => {
    if (Object.keys(MINOR_LEGAL_CAPACITY).includes(legalCapacity as string)) {
      setLegalCapacityCategory(LEGAL_CAPACITY_CATEGORY.minorKO);
    } else if (Object.keys(ADULT_LEGAL_CAPACITY_KO).includes(legalCapacity as string)) {
      setLegalCapacityCategory(LEGAL_CAPACITY_CATEGORY.adultKO);
    }

    switch (variant) {
      case 'primary':
        setTitle(TITLES.default);
        setContent(
          legalCapacityCategory === LEGAL_CAPACITY_CATEGORY.adultOK
            ? CONTENT.adultDashboard
            : CONTENT.notEmancipatedContent
        );
        break;

      case 'secondary':
        setTitle(TITLES.notEmancipated);
        setContent(CONTENT.empty);
        break;

      case 'tertiary':
        if (legalCapacityCategory === LEGAL_CAPACITY_CATEGORY.adultOK) {
          setTitle(`${TITLES.adultSupportOperation} ${location ? LOCATION_KEYS[location] : ''}`);
        } else {
          setTitle(TITLES.default);
          setContent(CONTENT.notEmancipatedContent);
        }
        break;
    }
  }, [legalCapacity, legalCapacityCategory, location, variant]);

  const sendTracking = (
    eventId: string,
    eventCategorie: string,
    eventNom: string,
    eventMetier: string,
    eventDetail1: string
  ) => {
    sendLogEvent('clic', {
      event_id: eventId,
      event_categorie: eventCategorie,
      event_nom: eventNom,
      event_metier: eventMetier,
      event_detail_1: eventDetail1,
      event_detail_2: investmentType && `${investmentType} - ${contractName}`,
    });
  };

  const selectPdfToDownload = () => {
    if (legalCapacityCategory === LEGAL_CAPACITY_CATEGORY.minorKO) {
      return URL_PDF_KYC_MINOR;
    } else if (legalCapacityCategory === LEGAL_CAPACITY_CATEGORY.adultKO) {
      return URL_PDF_KYC_MAJOR;
    }
    return '';
  };

  const handleNavigateToKYC = () => {
    sendTracking(
      'clic',
      'affichage popin mettre a jour informations',
      'mettre a jour informations',
      'espace client placement',
      'cta'
    );
    navigateToKYC();
  };

  const handleClose = () => {
    if (legalCapacityCategory === LEGAL_CAPACITY_CATEGORY.adultOK) {
      sendTracking(
        'clic',
        'affichage popin mettre a jour informations',
        'fermeture popin mettre a jour informations',
        'espace client placement',
        'cta'
      );
    } else {
      sendTracking(
        'clic',
        'affichage popin données personnelles mineur',
        'fermeture popin données personnelles mineur',
        'espace client placement',
        'cta'
      );
    }
    onClickClose();
  };

  return (
    <ModaleDefaut
      content={
        <div className={style.modalContentKyc}>
          {WarningIcon && <WarningIconModal />}
          <h3>{title}</h3>
          <p>{content}</p>

          {legalCapacityCategory !== LEGAL_CAPACITY_CATEGORY.adultOK && (
            <div className={style.modalKycList}>
              <span className={style.titleTextWarningKYC}>
                Pour effectuer cette mise à jour, voici la marche à suivre :
              </span>
              <ul className={style.listModalKoKYC}>
                <li>
                  Télécharger le formulaire{' '}
                  <a
                    className={style.linkModalKYC}
                    href={selectPdfToDownload()}
                    onClick={() =>
                      sendTracking(
                        'action',
                        'affichage popin données personnelles mineur',
                        'telechargement formulaire',
                        'espace client placement',
                        'cta'
                      )
                    }
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ci-joint
                  </a>{' '}
                </li>
                <li>Le faire remplir par le représentant légal</li>
                <li>
                  {`Renvoyer le document à l'adresse mail suivante : `}
                  <a className={style.linkModalKYC} href={`mailto:${URLS.MTP_EMAIL}`}>
                    {URLS.MTP_EMAIL}
                  </a>
                </li>
              </ul>
            </div>
          )}
        </div>
      }
      btnLabel={legalCapacityCategory === LEGAL_CAPACITY_CATEGORY.adultOK ? 'Mettre à jour mes informations' : 'Fermer'}
      btnVariant="primary"
      btnSize="large"
      onClickBtn={legalCapacityCategory === LEGAL_CAPACITY_CATEGORY.adultOK ? handleNavigateToKYC : handleClose}
      onClickClose={handleClose}
      size="large"
      mobileStyle="stick-to-bottom"
    />
  );
};

export default ContentModalKyc;
